import { useCallback, useState } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import financePayrollService, { UnlinkedMatch } from '@/services/financePayroll';

/**
 * A person was just linked by hand, and the same name sits unlinked on other
 * reports. Offer to apply the link across all of them at once.
 */
export type SpreadPrompt = {
  profileId: string;
  profileName: string;
  matches: UnlinkedMatch[];
};

type Result = {
  linkProfile: (itemId: string, profileId: string | null, profileName?: string) => Promise<void>;
  spread: SpreadPrompt | null;
  applySpread: (itemIds: string[]) => Promise<void>;
  dismissSpread: () => void;
  isApplying: boolean;
};

/**
 * Linking a payroll row to a contractor profile, plus the "link the rest too"
 * follow-up. Kept out of the container so the page stays presentational.
 */
export default function usePayrollProfileLinking(onChanged: () => void): Result {
  const toast = useToastContext();
  const [spread, setSpread] = useState<SpreadPrompt | null>(null);
  const [isApplying, setIsApplying] = useState(false);

  const linkProfile = useCallback(
    async (itemId: string, profileId: string | null, profileName?: string) => {
      try {
        await financePayrollService.updateItemProfile(itemId, profileId);
        onChanged();

        // Unlinking is a rejection — never follow it with a spread prompt.
        if (!profileId) return;

        const matches = await financePayrollService.getUnlinkedMatches(itemId);
        if (matches.length > 0) {
          setSpread({ profileId, profileName: profileName ?? 'this contractor', matches });
        }
      } catch {
        toast.showErrorFeedback('Failed to update profile link.');
      }
    },
    [onChanged, toast]
  );

  const applySpread = useCallback(
    async (itemIds: string[]) => {
      if (!spread || itemIds.length === 0) return;
      setIsApplying(true);
      try {
        await financePayrollService.linkItemsToProfile(itemIds, spread.profileId);
        onChanged();
        setSpread(null);
      } catch {
        toast.showErrorFeedback('Failed to link the other reports.');
      } finally {
        setIsApplying(false);
      }
    },
    [spread, onChanged, toast]
  );

  const dismissSpread = useCallback(() => setSpread(null), []);

  return { linkProfile, spread, applySpread, dismissSpread, isApplying };
}
